import React, { useState } from 'react';
import { Table, Button, Input, Space, Tag, message, Dropdown, Modal, Form, Select } from 'antd';
import { PlusOutlined, SearchOutlined, MoreOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card } from '../components/design-system/Card';
import api from '../services/api';

interface Commodity {
  id: string;
  code: string;
  name: string;
}

interface DocumentRequirement {
  id: string;
  documents: string;
  active: boolean;
  commodityId: string | null;
  commodity: Commodity | null;
  usageCount: number;
  createdAt: string;
}

interface DocumentRequirementsResponse {
  items: DocumentRequirement[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
  };
}

interface CommoditiesResponse {
  commodities: Commodity[];
}

const DocumentRequirements: React.FC = () => {
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedItem, setSelectedItem] = useState<DocumentRequirement | null>(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [form] = Form.useForm();

  const { data, isLoading, error } = useQuery<DocumentRequirementsResponse>({
    queryKey: ['document-requirements', searchTerm],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      params.append('limit', '200');
      return await api.get(`/document-requirements?${params.toString()}`);
    },
  });

  const { data: commoditiesData } = useQuery<CommoditiesResponse>({
    queryKey: ['commodities-active'],
    queryFn: async () => {
      return await api.get('/commodities?active=true&limit=200');
    },
  });

  const items = data?.items || [];
  const commodities = commoditiesData?.commodities || [];

  const createMutation = useMutation({
    mutationFn: async (values: { documents: string; commodityId?: string }) => {
      return await api.post('/document-requirements', values);
    },
    onSuccess: () => {
      message.success('Document requirement created');
      queryClient.invalidateQueries({ queryKey: ['document-requirements'] });
      handleModalClose();
    },
    onError: (error: any) => {
      message.error(error.response?.data?.error || 'Failed to create');
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, values }: { id: string; values: any }) => {
      return await api.patch(`/document-requirements/${id}`, values);
    },
    onSuccess: () => {
      message.success('Document requirement updated');
      queryClient.invalidateQueries({ queryKey: ['document-requirements'] });
      handleModalClose();
    },
    onError: (error: any) => {
      message.error(error.response?.data?.error || 'Failed to update');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      return await api.delete(`/document-requirements/${id}`);
    },
    onSuccess: () => {
      message.success('Document requirement deactivated');
      queryClient.invalidateQueries({ queryKey: ['document-requirements'] });
    },
    onError: (error: any) => {
      message.error(error.response?.data?.error || 'Failed to delete');
    },
  });

  const handleCreate = () => {
    setSelectedItem(null);
    setModalMode('create');
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (item: DocumentRequirement) => {
    setSelectedItem(item);
    setModalMode('edit');
    form.setFieldsValue({
      documents: item.documents,
      commodityId: item.commodityId,
      active: item.active,
    });
    setModalVisible(true);
  };

  const handleDelete = (id: string) => {
    deleteMutation.mutate(id);
  };

  const handleModalClose = () => {
    setModalVisible(false);
    setSelectedItem(null);
    form.resetFields();
  };

  const handleSubmit = (values: any) => {
    if (modalMode === 'create') {
      createMutation.mutate(values);
    } else if (selectedItem) {
      updateMutation.mutate({ id: selectedItem.id, values });
    }
  };

  const columns = [
    {
      title: 'Document Requirements',
      dataIndex: 'documents',
      key: 'documents',
      sorter: (a: DocumentRequirement, b: DocumentRequirement) => a.documents.localeCompare(b.documents),
      render: (text: string) => <div style={{ maxWidth: 500 }}>{text}</div>,
    },
    {
      title: 'Commodity',
      key: 'commodity',
      width: 150,
      render: (record: DocumentRequirement) => (
        record.commodity ? (
          <Tag color="blue">{record.commodity.code}</Tag>
        ) : (
          <span style={{ color: 'var(--text-tertiary)' }}>All</span>
        )
      ),
    },
    {
      title: 'Usage',
      dataIndex: 'usageCount',
      key: 'usageCount',
      width: 100,
      sorter: (a: DocumentRequirement, b: DocumentRequirement) => a.usageCount - b.usageCount,
    },
    {
      title: 'Status',
      dataIndex: 'active',
      key: 'active',
      width: 100,
      filters: [
        { text: 'Active', value: true },
        { text: 'Inactive', value: false },
      ],
      onFilter: (value: any, record: DocumentRequirement) => record.active === value,
      render: (active: boolean) => (
        <Tag color={active ? 'success' : 'default'}>
          {active ? 'ACTIVE' : 'INACTIVE'}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 80,
      render: (record: DocumentRequirement) => (
        <Dropdown
          menu={{
            items: [
              {
                key: 'edit',
                icon: <EditOutlined />,
                label: 'Edit',
                onClick: () => handleEdit(record),
              },
              {
                key: 'delete',
                icon: <DeleteOutlined />,
                label: 'Deactivate',
                danger: true,
                onClick: () => handleDelete(record.id),
              },
            ],
          }}
          trigger={['click']}
        >
          <Button type="text" icon={<MoreOutlined />} size="small" />
        </Dropdown>
      ),
    },
  ];

  if (error) {
    return (
      <div style={{ padding: '48px', textAlign: 'center' }}>
        <h2 className="text-h2" style={{ color: 'var(--color-negative)' }}>Unable to Load Document Requirements</h2>
        <Button type="primary" onClick={() => window.location.reload()} style={{ marginTop: '24px' }}>
          Refresh Page
        </Button>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div>
          <h1 className="text-h1">Document Requirements</h1>
          <p className="text-card-body" style={{ color: 'var(--text-secondary)', marginTop: '4px' }}>
            Manage document requirement text for contract language
          </p>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate} size="large">
          Add Requirement
        </Button>
      </div>

      <Card style={{ marginBottom: '24px' }}>
        <Space size="middle" style={{ width: '100%', flexWrap: 'wrap' }}>
          <Input
            placeholder="Search document requirements..."
            prefix={<SearchOutlined />}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ width: 300 }}
            allowClear
          />
          <div className="text-card-body" style={{ color: 'var(--text-secondary)' }}>
            {items.length} requirements found
          </div>
        </Space>
      </Card>

      <Card>
        <Table
          columns={columns}
          dataSource={items}
          loading={isLoading}
          rowKey="id"
          pagination={{
            showSizeChanger: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
            pageSizeOptions: [10, 20, 50, 100],
          }}
        />
      </Card>

      <Modal
        title={modalMode === 'create' ? 'Add Document Requirement' : 'Edit Document Requirement'}
        open={modalVisible}
        onCancel={handleModalClose}
        footer={null}
        width={600}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="documents"
            label="Document Requirements Text"
            rules={[{ required: true, message: 'Please enter document requirements' }]}
          >
            <Input.TextArea
              rows={4}
              placeholder="e.g., Commercial Invoice, Packing List, Certificate of Origin, Bill of Lading"
            />
          </Form.Item>

          <Form.Item name="commodityId" label="Commodity (Optional)">
            <Select
              placeholder="Select commodity (leave empty for all)"
              allowClear
              showSearch
              optionFilterProp="children"
            >
              {commodities.map((c) => (
                <Select.Option key={c.id} value={c.id}>
                  {c.code} - {c.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          {modalMode === 'edit' && (
            <Form.Item name="active" label="Status" valuePropName="checked">
              <Select>
                <Select.Option value={true}>Active</Select.Option>
                <Select.Option value={false}>Inactive</Select.Option>
              </Select>
            </Form.Item>
          )}

          <Form.Item style={{ marginBottom: 0, marginTop: '24px' }}>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button onClick={handleModalClose}>Cancel</Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={createMutation.isPending || updateMutation.isPending}
              >
                {modalMode === 'create' ? 'Create' : 'Save'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default DocumentRequirements;
